import { CombatStyle, SpecialAttackDef, WeaponDef, WeaponLevel } from "./types";

// Builds a weapon's level table from its first level and what each later level
// changes, so each level only states its differences.
function levels(
    base: Omit<WeaponLevel, "description"> & { description?: string },
    ...changes: (Partial<WeaponLevel> & { description: string })[]
): WeaponLevel[] {
    const result: WeaponLevel[] = [{ description: "", ...base }];
    for (const change of changes) {
        result.push({ ...result[result.length - 1], ...change });
    }
    return result;
}

// Levels are kept small: a maxed weapon is about twice as strong as a new one,
// so the run's difficulty, not the upgrades, decides how it goes. Rarer level
// up rolls add extra damage on top (see rarity.ts).

const SCIMITARS = [1321, 1323, 1325, 1327, 1329, 1331, 1333, 4587];
const KNIVES = [864, 863, 865, 869, 866, 867, 868, 868];
const TWO_HANDED_SWORDS = [1307, 1309, 1311, 1313, 1315, 1317, 1319, 7158];
// Normal, oak, willow, maple, yew and magic
const SHORTBOWS = [841, 843, 849, 853, 857, 861];
// Arrows in flight: one model the metals recolour (the Twisted bow's amethyst
// arrows are another)
const ARROWS = { bronze: 10, iron: 9, steel: 11, black: 14, mithril: 12, adamant: 13, rune: 15 };
// Bronze to rune, then dragon
const THROWNAXES = [800, 801, 802, 803, 804, 805, 20849, 20849];
// Thrownaxes in flight, spinning: one model the metals recolour, and the
// dragon thrownaxe's own
const THROWNAXE_PROJECTILES = {
    bronze: 36,
    iron: 35,
    steel: 37,
    mithril: 38,
    adamant: 39,
    rune: 41,
    dragon: 1319,
};
// Air, water, earth and fire, then mind, chaos, death and blood (for extra
// projectiles)
const RUNES = [556, 555, 557, 554, 558, 562, 560, 565];

// Fire spells: cast, projectile and impact spot animations, and the client's
// cast and hit sounds (firestrike_cast_and_fire, firestrike_hit, ...)
const FIRE_STRIKE = {
    castSpotAnim: 99,
    projectileSpotAnim: 100,
    impactSpotAnim: 101,
    sound: 160,
    hitSound: 161,
};
const FIRE_BOLT = {
    castSpotAnim: 126,
    projectileSpotAnim: 127,
    impactSpotAnim: 128,
    sound: 157,
    hitSound: 158,
};
const FIRE_BLAST = {
    castSpotAnim: 129,
    projectileSpotAnim: 130,
    impactSpotAnim: 131,
    sound: 155,
    hitSound: 156,
};
const FIRE_WAVE = {
    castSpotAnim: 155,
    projectileSpotAnim: 156,
    impactSpotAnim: 157,
    sound: 162,
    hitSound: 163,
};
// Fire Surge's own sounds aren't known, so Fire Wave's stand in
const FIRE_SURGE = {
    castSpotAnim: 1464,
    projectileSpotAnim: 1465,
    impactSpotAnim: 1466,
    sound: 162,
    hitSound: 163,
};

// Crystal spells from the Gauntlet: the projectile and its impact, bigger as
// the staff is attuned, and the Hunllef's own orb once it's perfected
const CRYSTAL_STAFF = {
    basic: { projectileSpotAnim: 1701, impactSpotAnim: 1703 },
    attuned: { projectileSpotAnim: 1713, impactSpotAnim: 1709 },
    perfected: { projectileSpotAnim: 1707, impactSpotAnim: 1715 },
};

// What the Ivandis and Blisterwood flails are made for
const VAMPYRES_AND_UNDEAD: WeaponDef["slayer"] = { tags: ["vampyre", "undead"], damage: 0.5 };

// A two-handed sword's slash
const GODSWORD_SEQ = 7045;
// hacksword_slash stands in for a two-handed sword's
const GODSWORD_SOUND = 2500;

// Every godsword swings the same, the blade before it has a hilt too
function godswordLevels(objId?: number): WeaponLevel[] {
    return levels(
        {
            damage: 44,
            cooldown: 1.9,
            amount: 1,
            area: 2.8,
            arc: 0.95,
            pierce: 0,
            speed: 0,
            duration: 0,
            knockback: 1,
            objId,
        },
        { damage: 53, description: "+9 damage." },
        { area: 3.1, description: "+11% reach." },
        { cooldown: 1.7, description: "Swings a little faster." },
        { damage: 66, description: "+13 damage." },
    );
}

// The hilt sets a godsword's special attack
function godsword(
    id: string,
    name: string,
    objId: number,
    description: string,
    special: SpecialAttackDef,
): WeaponDef {
    return {
        id,
        name,
        kind: "sweep",
        description: `A heavy two-handed sweep. ${description}`,
        iconObjId: objId,
        playerSeq: GODSWORD_SEQ,
        sound: GODSWORD_SOUND,
        hitSound: -1,
        rarity: "rare",
        style: "melee",
        family: "godsword",
        evolution: { into: "ancient_godsword", requiresPassive: "piety" },
        special,
        levels: godswordLevels(objId),
    };
}

// The blade takes its hilt at this level
const HILT_LEVEL = 2;

// Having beaten the Desert, a burst's first level picks the run's Ancient spell
const ANCIENT_SPELL_PICK = {
    level: HILT_LEVEL,
    into: [
        { weaponId: "ice_burst", hiltObjId: 4675 },
        { weaponId: "smoke_burst", hiltObjId: 25491 },
        { weaponId: "shadow_burst", hiltObjId: 25492 },
        { weaponId: "blood_burst", hiltObjId: 25489 },
    ],
};

// Explicit damage curves give all three evolution levels. A single number
// keeps the original 15%/30% progression for weapons with utility tradeoffs.
function evolvedLevels(
    first: Omit<WeaponLevel, "description" | "damage"> & {
        description: string;
        damage: number | readonly [number, number, number];
    },
): WeaponLevel[] {
    const [initial, second, third] =
        typeof first.damage === "number"
            ? [first.damage, Math.round(first.damage * 1.15), Math.round(first.damage * 1.3)]
            : first.damage;
    return levels(
        { ...first, damage: initial },
        { damage: second, description: `+${second - initial} damage.` },
        { damage: third, description: `+${third - second} damage.` },
    );
}

export const WEAPONS: WeaponDef[] = [
    {
        id: "scimitar",
        name: "Scimitar",
        kind: "slash",
        description: "Slashes at the closest enemy. Upgrades through the metals to dragon.",
        iconObjId: 1321,
        playerSeq: 390,
        // hacksword_slash
        sound: 2500,
        hitSound: -1,
        rarity: "common",
        levels: levels(
            {
                name: "Bronze scimitar",
                damage: 16,
                cooldown: 1.35,
                amount: 1,
                area: 2.2,
                pierce: 0,
                speed: 0,
                duration: 0,
                knockback: 0.5,
                objId: SCIMITARS[0],
            },
            {
                name: "Iron scimitar",
                objId: SCIMITARS[1],
                amount: 2,
                description: "Also slashes behind you.",
            },
            { name: "Steel scimitar", objId: SCIMITARS[2], damage: 21, description: "+5 damage." },
            { name: "Black scimitar", objId: SCIMITARS[3], area: 2.4, description: "+10% reach." },
            {
                name: "Mithril scimitar",
                objId: SCIMITARS[4],
                damage: 24,
                description: "+3 damage.",
            },
            {
                name: "Adamant scimitar",
                objId: SCIMITARS[5],
                cooldown: 1.25,
                description: "Slashes a little faster.",
            },
            {
                name: "Rune scimitar",
                objId: SCIMITARS[6],
                damage: 27,
                area: 2.6,
                description: "+3 damage, +8% reach.",
            },
            {
                name: "Dragon scimitar",
                objId: SCIMITARS[7],
                damage: 32,
                cooldown: 1.15,
                description: "+5 damage, slashes a little faster.",
            },
        ),
        evolution: { into: "abyssal_whip", requiresPassive: "piety" },
    },
    {
        id: "abyssal_whip",
        name: "Abyssal whip",
        kind: "slash",
        description: "Evolved scimitar. Lashes on both sides, fast and far.",
        iconObjId: 4151,
        playerSeq: 1658,
        // whip
        sound: 2720,
        hitSound: -1,
        rarity: "veryRare",
        evolved: true,
        levels: evolvedLevels({
            name: "Abyssal whip",
            damage: [33, 36, 38],
            cooldown: 0.95,
            amount: 2,
            area: 3.1,
            pierce: 0,
            speed: 0,
            duration: 0,
            knockback: 1.1,
            objId: 4151,
            description: "Evolved from the dragon scimitar with Piety.",
        }),
    },
    {
        id: "fire_spells",
        name: "Fire spells",
        kind: "missile",
        description: "Fires at the nearest enemy. Learns Bolt, Blast and Wave.",
        iconObjId: 1387,
        playerSeq: 711,
        // firestrike_cast_and_fire, firestrike_hit
        sound: 160,
        hitSound: 161,
        rarity: "common",
        damageStyle: "magic",
        levels: levels(
            {
                name: "Fire Strike",
                ...FIRE_STRIKE,
                damage: 9,
                cooldown: 1.1,
                amount: 1,
                area: 0,
                pierce: 0,
                speed: 9,
                duration: 0,
                knockback: 0.25,
                objId: 1387,
            },
            { amount: 2, description: "Casts one more Fire Strike." },
            {
                name: "Fire Bolt",
                ...FIRE_BOLT,
                damage: 12,
                description: "Learns Fire Bolt: +3 damage.",
            },
            { cooldown: 1.0, description: "Casts a little faster." },
            {
                name: "Fire Blast",
                ...FIRE_BLAST,
                damage: 15,
                area: 0.9,
                description: "Learns Fire Blast: +3 damage, small splash.",
            },
            { amount: 3, description: "Casts one more blast." },
            {
                name: "Fire Wave",
                ...FIRE_WAVE,
                damage: 18,
                area: 1.2,
                description: "Learns Fire Wave: +3 damage, bigger splash.",
            },
            { cooldown: 0.9, pierce: 1, description: "Faster; waves pass through an enemy." },
        ),
        evolution: { into: "flames_of_zamorak", requiresPassive: "augury" },
    },
    {
        id: "flames_of_zamorak",
        name: "Flames of Zamorak",
        kind: "strike",
        description:
            "Evolved fire spells. Pillars of Zamorak's fire burst up under enemies all around you; bosses and elites are burned three times more.",
        iconObjId: 2417,
        // The god spell cast; Saradomin Strike's sound stands in for the flames'
        playerSeq: 811,
        sound: 1659,
        hitSound: -1,
        rarity: "veryRare",
        evolved: true,
        // More pillars on big things, which a strike only hits once
        bigHits: [0.75, 0.75, 0.75],
        levels: evolvedLevels({
            damage: 30,
            cooldown: 2,
            amount: 6,
            area: 1.4,
            pierce: 0,
            speed: 0,
            duration: 0,
            knockback: 0.3,
            objId: 2417,
            impactSpotAnim: 78,
            description: "Evolved from Fire Wave with Augury.",
        }),
    },
    {
        id: "throwing_knives",
        name: "Throwing knives",
        kind: "knives",
        description: "Throws knives at nearby enemies. Upgrades through the metals.",
        iconObjId: 864,
        playerSeq: 806,
        // throwingknife
        sound: 2707,
        hitSound: -1,
        rarity: "common",
        levels: levels(
            {
                name: "Bronze knives",
                damage: 6,
                cooldown: 0.65,
                amount: 1,
                area: 0,
                pierce: 1,
                speed: 15,
                duration: 0,
                knockback: 0.15,
                objId: KNIVES[0],
            },
            {
                name: "Iron knives",
                objId: KNIVES[1],
                amount: 2,
                description: "Throws one more knife.",
            },
            { name: "Steel knives", objId: KNIVES[2], damage: 8, description: "+2 damage." },
            {
                name: "Black knives",
                objId: KNIVES[3],
                cooldown: 0.6,
                description: "Throws a little faster.",
            },
            {
                name: "Mithril knives",
                objId: KNIVES[4],
                pierce: 2,
                description: "Knives pass through one more enemy.",
            },
            {
                name: "Adamant knives",
                objId: KNIVES[5],
                amount: 3,
                description: "Throws one more knife.",
            },
            { name: "Rune knives", objId: KNIVES[6], damage: 10, description: "+2 damage." },
            {
                name: "Rune knives",
                objId: KNIVES[7],
                amount: 4,
                description: "Throws one more knife.",
            },
        ),
        evolution: { into: "toktz_xil_ul", requiresPassive: "obsidian_armour" },
    },
    {
        id: "toktz_xil_ul",
        name: "Toktz-xil-ul",
        kind: "knives",
        description: "Evolved throwing knives. Obsidian rings that go through everything.",
        iconObjId: 6522,
        playerSeq: 2614,
        sound: 2707,
        hitSound: -1,
        rarity: "veryRare",
        evolved: true,
        unlock: { kind: "killBoss", mapId: "karamja", enemyId: "ket_zek" },
        levels: evolvedLevels({
            damage: [11, 12, 13],
            cooldown: 0.5,
            amount: 5,
            area: 0,
            pierce: 99,
            speed: 15,
            duration: 0,
            knockback: 0.3,
            objId: 6522,
            description: "Evolved from rune knives with Obsidian armour.",
        }),
    },
    {
        id: "ice_burst",
        name: "Ice Burst",
        kind: "aura",
        description: "Bursts of ice around you that hurt and freeze.",
        iconObjId: 4675,
        playerSeq: 1979,
        // ice_burst_impact
        sound: 170,
        hitSound: -1,
        rarity: "common",
        // One Ancient burst at a time
        family: "ancient_burst",
        hilts: ANCIENT_SPELL_PICK,
        freezes: true,
        levels: levels(
            {
                name: "Ice Burst",
                damage: 5,
                cooldown: 2.3,
                amount: 1,
                area: 2.3,
                pierce: 0,
                speed: 0,
                duration: 0.6,
                knockback: 0.1,
                impactSpotAnim: 363,
            },
            { area: 2.6, description: "+13% radius." },
            { damage: 6, description: "+1 damage." },
            { cooldown: 2.1, description: "Bursts a little more often." },
            { duration: 0.85, description: "Freezes for longer." },
            { area: 2.9, description: "+12% radius." },
            { damage: 8, description: "+2 damage." },
            { cooldown: 1.9, description: "Bursts a little more often." },
        ),
        evolution: { into: "ice_barrage", requiresPassive: "protect_melee" },
    },
    {
        id: "ice_barrage",
        name: "Ice Barrage",
        kind: "aura",
        description: "Evolved Ice Burst. A huge freezing blast.",
        iconObjId: 4675,
        playerSeq: 1979,
        // ice_barrage_impact
        sound: 168,
        hitSound: -1,
        rarity: "veryRare",
        evolved: true,
        // Still the one Ancient spell at a time
        family: "ancient_burst",
        freezes: true,
        levels: evolvedLevels({
            name: "Ice Barrage",
            damage: [11, 12, 13],
            cooldown: 1.6,
            amount: 1,
            area: 3.8,
            pierce: 0,
            speed: 0,
            duration: 1.5,
            knockback: 0.1,
            impactSpotAnim: 369,
            description: "Evolved from Ice Burst with Protect from Melee.",
        }),
    },
    // The rest of the Ancient bursts, from Desert Treasure's guardians; the
    // player holds the sceptre
    {
        id: "smoke_burst",
        name: "Smoke Burst",
        kind: "aura",
        description: "Bursts of smoke around you that poison what they hit.",
        iconObjId: 25491,
        playerSeq: 1979,
        // smoke_burst_impact
        sound: 182,
        hitSound: -1,
        rarity: "common",
        family: "ancient_burst",
        hilts: ANCIENT_SPELL_PICK,
        pickOnly: true,
        unlock: { kind: "beatMap", mapId: "desert" },
        levels: levels(
            {
                name: "Smoke Burst",
                damage: 4,
                cooldown: 2.3,
                amount: 1,
                area: 2.3,
                pierce: 0,
                speed: 0,
                duration: 3,
                venom: 2,
                knockback: 0.1,
                impactSpotAnim: 389,
                objId: 25491,
            },
            { area: 2.6, description: "+13% radius." },
            { venom: 3, description: "Stronger poison: 3 damage a second." },
            { cooldown: 2.1, description: "Bursts a little more often." },
            { damage: 6, description: "+2 damage." },
            { area: 2.9, description: "+12% radius." },
            { venom: 4, duration: 4, description: "Stronger poison that lasts longer." },
            { cooldown: 1.9, description: "Bursts a little more often." },
        ),
        evolution: { into: "smoke_barrage", requiresPassive: "ancient_sceptre" },
    },
    {
        id: "shadow_burst",
        name: "Shadow Burst",
        kind: "aura",
        description:
            "Bursts of shadow around you. What they hit takes 20% more damage for a while.",
        iconObjId: 25492,
        playerSeq: 1979,
        // shadow_burst_impact
        sound: 177,
        hitSound: -1,
        rarity: "common",
        family: "ancient_burst",
        drain: 0.2,
        hilts: ANCIENT_SPELL_PICK,
        pickOnly: true,
        unlock: { kind: "beatMap", mapId: "desert" },
        levels: levels(
            {
                name: "Shadow Burst",
                damage: 6,
                cooldown: 2.3,
                amount: 1,
                area: 2.3,
                pierce: 0,
                speed: 0,
                duration: 4,
                knockback: 0.1,
                impactSpotAnim: 382,
                objId: 25492,
            },
            { area: 2.6, description: "+13% radius." },
            { damage: 7, description: "+1 damage." },
            { cooldown: 2.1, description: "Bursts a little more often." },
            { duration: 6, description: "The shadow lingers longer." },
            { area: 2.9, description: "+12% radius." },
            { damage: 9, description: "+2 damage." },
            { cooldown: 1.9, description: "Bursts a little more often." },
        ),
        evolution: { into: "shadow_barrage", requiresPassive: "ancient_sceptre" },
    },
    {
        id: "blood_burst",
        name: "Blood Burst",
        kind: "aura",
        description: "Bursts of blood around you. Now and then a hit heals you by 2.",
        iconObjId: 25489,
        playerSeq: 1979,
        // blood_burst_impact
        sound: 105,
        hitSound: -1,
        rarity: "common",
        family: "ancient_burst",
        lifesteal: { chance: 0.05, fraction: 0.5, max: 2, spotAnim: 1542 },
        hilts: ANCIENT_SPELL_PICK,
        pickOnly: true,
        unlock: { kind: "beatMap", mapId: "desert" },
        levels: levels(
            {
                name: "Blood Burst",
                damage: 6,
                cooldown: 2.3,
                amount: 1,
                area: 2.3,
                pierce: 0,
                speed: 0,
                duration: 0,
                knockback: 0.1,
                impactSpotAnim: 376,
                objId: 25489,
            },
            { area: 2.6, description: "+13% radius." },
            { damage: 7, description: "+1 damage." },
            { cooldown: 2.1, description: "Bursts a little more often." },
            { damage: 8, description: "+1 damage." },
            { area: 2.9, description: "+12% radius." },
            { damage: 10, description: "+2 damage." },
            { cooldown: 1.9, description: "Bursts a little more often." },
        ),
        evolution: { into: "blood_barrage", requiresPassive: "rapid_heal" },
    },
    // The bursts' evolutions, like Ice Barrage: a bigger blast, and more of
    // what each one does. Ice Barrage's sound stands in for theirs.
    {
        id: "smoke_barrage",
        name: "Smoke Barrage",
        kind: "aura",
        description: "Evolved Smoke Burst. A huge blast of smoke that poisons hard and long.",
        iconObjId: 25491,
        playerSeq: 1979,
        sound: 168,
        hitSound: -1,
        rarity: "veryRare",
        evolved: true,
        family: "ancient_burst",
        levels: evolvedLevels({
            name: "Smoke Barrage",
            damage: [7, 8, 9],
            cooldown: 1.6,
            amount: 1,
            area: 3.8,
            pierce: 0,
            speed: 0,
            duration: 5,
            venom: 7,
            knockback: 0.1,
            impactSpotAnim: 391,
            objId: 25491,
            description: "Evolved from Smoke Burst with the Ancient sceptre.",
        }),
    },
    {
        id: "shadow_barrage",
        name: "Shadow Barrage",
        kind: "aura",
        description:
            "Evolved Shadow Burst. A huge blast of shadow; what it hits takes 30% more damage for a while.",
        iconObjId: 25492,
        playerSeq: 1979,
        sound: 168,
        hitSound: -1,
        rarity: "veryRare",
        evolved: true,
        family: "ancient_burst",
        drain: 0.3,
        levels: evolvedLevels({
            name: "Shadow Barrage",
            damage: [11, 12, 13],
            cooldown: 1.6,
            amount: 1,
            area: 3.8,
            pierce: 0,
            speed: 0,
            duration: 7,
            knockback: 0.1,
            impactSpotAnim: 383,
            objId: 25492,
            description: "Evolved from Shadow Burst with the Ancient sceptre.",
        }),
    },
    {
        id: "blood_barrage",
        name: "Blood Barrage",
        kind: "aura",
        description:
            "Evolved Blood Burst. A huge blast of blood. Now and then a hit heals you by 4.",
        iconObjId: 25489,
        playerSeq: 1979,
        sound: 168,
        hitSound: -1,
        rarity: "veryRare",
        evolved: true,
        family: "ancient_burst",
        lifesteal: { chance: 0.08, fraction: 0.5, max: 4, spotAnim: 1542 },
        levels: evolvedLevels({
            name: "Blood Barrage",
            damage: [12, 14, 15],
            cooldown: 1.6,
            amount: 1,
            area: 3.8,
            pierce: 0,
            speed: 0,
            duration: 0,
            knockback: 0.1,
            impactSpotAnim: 377,
            objId: 25489,
            description: "Evolved from Blood Burst with Rapid Heal.",
        }),
    },
    {
        id: "saradomin_strike",
        name: "Saradomin Strike",
        kind: "strike",
        description: "Saradomin strikes random enemies around you.",
        iconObjId: 2415,
        playerSeq: 811,
        // saradomin_strike
        sound: 1659,
        hitSound: -1,
        rarity: "uncommon",
        levels: levels(
            {
                damage: 15,
                cooldown: 3.3,
                amount: 2,
                area: 0.6,
                pierce: 0,
                speed: 0,
                duration: 0,
                knockback: 0,
                impactSpotAnim: 76,
            },
            { amount: 3, description: "Strikes one more enemy." },
            { damage: 18, description: "+3 damage." },
            { area: 1.0, description: "Strikes hit around the target too." },
            { cooldown: 3.0, description: "Strikes a little more often." },
            { amount: 4, description: "Strikes one more enemy." },
            { damage: 22, description: "+4 damage." },
            { amount: 5, description: "Strikes one more enemy." },
        ),
        evolution: { into: "undead_grasp", requiresPassive: "book_of_the_dead" },
    },
    {
        id: "undead_grasp",
        name: "Undead Grasp",
        kind: "strike",
        description:
            "Evolved Saradomin Strike. Undead hands burst out of the ground and hold what they grab.",
        iconObjId: 25818,
        // The god spell cast; Shadow Burst's darkness for the hands
        playerSeq: 811,
        sound: 1659,
        hitSound: -1,
        rarity: "veryRare",
        evolved: true,
        freezes: true,
        unlock: { kind: "killBoss", mapId: "kourend", enemyId: "skotizo" },
        levels: evolvedLevels({
            damage: [23, 24, 26],
            cooldown: 2.6,
            amount: 6,
            area: 1.2,
            pierce: 0,
            speed: 0,
            duration: 1.5,
            knockback: 0,
            impactSpotAnim: 382,
            description: "Evolved from Saradomin Strike with the Book of the dead.",
        }),
    },
    {
        id: "two_handed_sword",
        name: "Two-handed sword",
        kind: "sweep",
        description: "A heavy sweep in front of you. Upgrades through the metals to dragon.",
        iconObjId: TWO_HANDED_SWORDS[0],
        playerSeq: GODSWORD_SEQ,
        sound: GODSWORD_SOUND,
        hitSound: -1,
        rarity: "common",
        levels: levels(
            {
                name: "Bronze 2h sword",
                damage: 19,
                cooldown: 1.6,
                amount: 1,
                area: 2.5,
                arc: 0.9,
                pierce: 0,
                speed: 0,
                duration: 0,
                knockback: 0.8,
                objId: TWO_HANDED_SWORDS[0],
            },
            {
                name: "Iron 2h sword",
                objId: TWO_HANDED_SWORDS[1],
                amount: 2,
                description: "Also sweeps behind you.",
            },
            {
                name: "Steel 2h sword",
                objId: TWO_HANDED_SWORDS[2],
                damage: 23,
                description: "+4 damage.",
            },
            {
                name: "Black 2h sword",
                objId: TWO_HANDED_SWORDS[3],
                area: 2.75,
                description: "+10% reach.",
            },
            {
                name: "Mithril 2h sword",
                objId: TWO_HANDED_SWORDS[4],
                damage: 27,
                description: "+4 damage.",
            },
            {
                name: "Adamant 2h sword",
                objId: TWO_HANDED_SWORDS[5],
                cooldown: 1.45,
                description: "Swings a little faster.",
            },
            {
                name: "Rune 2h sword",
                objId: TWO_HANDED_SWORDS[6],
                arc: 1.05,
                description: "Sweeps a wider arc.",
            },
            {
                name: "Dragon 2h sword",
                objId: TWO_HANDED_SWORDS[7],
                damage: 32,
                description: "+5 damage.",
            },
        ),
        evolution: { into: "colossal_blade", requiresPassive: "dizanas_quiver" },
    },
    {
        id: "colossal_blade",
        name: "Colossal blade",
        kind: "sweep",
        description:
            "Evolved 2h sword. A huge sweep on both sides. Bosses and elites are hit again.",
        iconObjId: 27021,
        playerSeq: GODSWORD_SEQ,
        sound: GODSWORD_SOUND,
        hitSound: -1,
        rarity: "veryRare",
        evolved: true,
        bigHits: [0.5],
        unlock: { kind: "beatMap", mapId: "varlamore" },
        levels: evolvedLevels({
            damage: [42, 45, 48],
            cooldown: 1.5,
            amount: 2,
            area: 3.6,
            arc: 1.2,
            pierce: 0,
            speed: 0,
            duration: 0,
            knockback: 1.4,
            objId: 27021,
            description: "Evolved from the dragon 2h sword with Dizana's quiver.",
        }),
    },
    {
        id: "shortbow",
        name: "Shortbow",
        kind: "missile",
        description:
            "Shoots arrows at the nearest enemy. Upgrades through the woods to magic, the arrows through the metals.",
        iconObjId: SHORTBOWS[0],
        // The bows' shot; throwingknife stands in for its sound
        playerSeq: 426,
        sound: 2707,
        hitSound: -1,
        rarity: "common",
        levels: levels(
            {
                name: "Shortbow",
                damage: 8,
                cooldown: 0.9,
                amount: 1,
                area: 0,
                pierce: 0,
                speed: 16,
                duration: 0,
                knockback: 0.2,
                objId: SHORTBOWS[0],
                projectileSpotAnim: ARROWS.bronze,
            },
            {
                name: "Oak shortbow",
                objId: SHORTBOWS[1],
                projectileSpotAnim: ARROWS.iron,
                amount: 2,
                description: "Fires one more arrow.",
            },
            {
                name: "Willow shortbow",
                objId: SHORTBOWS[2],
                projectileSpotAnim: ARROWS.steel,
                damage: 10,
                description: "+2 damage.",
            },
            {
                name: "Willow shortbow",
                projectileSpotAnim: ARROWS.black,
                pierce: 1,
                description: "Black arrows: they pass through an enemy.",
            },
            {
                name: "Maple shortbow",
                objId: SHORTBOWS[3],
                projectileSpotAnim: ARROWS.mithril,
                cooldown: 0.8,
                description: "Fires a little faster.",
            },
            {
                name: "Yew shortbow",
                objId: SHORTBOWS[4],
                projectileSpotAnim: ARROWS.adamant,
                amount: 3,
                description: "Fires one more arrow.",
            },
            {
                name: "Magic shortbow",
                objId: SHORTBOWS[5],
                projectileSpotAnim: ARROWS.rune,
                damage: 13,
                description: "+3 damage.",
            },
            {
                name: "Magic shortbow",
                cooldown: 0.7,
                pierce: 2,
                description: "Faster; arrows pass through one more enemy.",
            },
        ),
        evolution: { into: "dark_bow", requiresPassive: "rigour" },
    },
    {
        id: "dark_bow",
        name: "Dark bow",
        kind: "missile",
        description:
            "Evolved shortbow. Two heavy dragon arrows at a time, Descent of Dragons, that blow up where they hit.",
        iconObjId: 11235,
        playerSeq: 426,
        sound: 2707,
        hitSound: -1,
        rarity: "veryRare",
        evolved: true,
        levels: evolvedLevels({
            name: "Dark bow",
            damage: [33, 35, 38],
            cooldown: 1,
            amount: 2,
            area: 0.8,
            pierce: 2,
            speed: 14,
            duration: 0,
            knockback: 0.6,
            objId: 11235,
            // Descent of Dragons' arrows and their impact
            projectileSpotAnim: 1099,
            impactSpotAnim: 1100,
            description: "Evolved from the magic shortbow with Rigour.",
        }),
    },
    {
        id: "throwing_axes",
        name: "Throwing axes",
        kind: "axes",
        description:
            "Throws axes at the nearest enemy that bounce on from one to the next. Upgrades through the metals to dragon.",
        iconObjId: THROWNAXES[0],
        // throwingknife stands in for a thrownaxe's sound
        playerSeq: 806,
        sound: 2707,
        hitSound: -1,
        rarity: "common",
        levels: levels(
            {
                name: "Bronze thrownaxe",
                damage: 9,
                cooldown: 1.1,
                amount: 1,
                area: 0,
                pierce: 0,
                bounce: 1,
                speed: 12,
                duration: 0,
                knockback: 0.2,
                objId: THROWNAXES[0],
                projectileSpotAnim: THROWNAXE_PROJECTILES.bronze,
            },
            {
                name: "Iron thrownaxe",
                objId: THROWNAXES[1],
                projectileSpotAnim: THROWNAXE_PROJECTILES.iron,
                bounce: 2,
                description: "Axes bounce on to one more enemy.",
            },
            {
                name: "Steel thrownaxe",
                objId: THROWNAXES[2],
                projectileSpotAnim: THROWNAXE_PROJECTILES.steel,
                damage: 11,
                description: "+2 damage.",
            },
            {
                name: "Mithril thrownaxe",
                objId: THROWNAXES[3],
                projectileSpotAnim: THROWNAXE_PROJECTILES.mithril,
                amount: 2,
                description: "Throws one more axe.",
            },
            {
                name: "Adamant thrownaxe",
                objId: THROWNAXES[4],
                projectileSpotAnim: THROWNAXE_PROJECTILES.adamant,
                bounce: 3,
                description: "Axes bounce on to one more enemy.",
            },
            {
                name: "Rune thrownaxe",
                objId: THROWNAXES[5],
                projectileSpotAnim: THROWNAXE_PROJECTILES.rune,
                damage: 13,
                cooldown: 1,
                description: "+2 damage, throws a little faster.",
            },
            {
                name: "Dragon thrownaxe",
                objId: THROWNAXES[6],
                projectileSpotAnim: THROWNAXE_PROJECTILES.dragon,
                bounce: 5,
                description: "Axes bounce on to two more enemies.",
            },
            {
                name: "Dragon thrownaxe",
                objId: THROWNAXES[7],
                amount: 3,
                description: "Throws one more axe.",
            },
        ),
        evolution: { into: "chain_hit_axes", requiresPassive: "tome_of_fire" },
    },
    {
        id: "chain_hit_axes",
        name: "Chain-hit axes",
        kind: "axes",
        description:
            "Evolved throwing axes. Morrigan's axes chain-hit through a crowd, bouncing on to up to nine more.",
        iconObjId: 22634,
        playerSeq: 806,
        sound: 2707,
        hitSound: -1,
        rarity: "veryRare",
        evolved: true,
        levels: evolvedLevels({
            name: "Chain-hit axes",
            damage: [15, 17, 18],
            cooldown: 0.9,
            amount: 3,
            area: 0,
            pierce: 0,
            bounce: 9,
            speed: 14,
            duration: 0,
            knockback: 0.3,
            objId: 22634,
            // Morrigan's throwing axe, spinning
            projectileSpotAnim: 1623,
            description: "Evolved from dragon thrownaxes with the Tome of fire.",
        }),
    },
    {
        id: "elemental_runes",
        name: "Elemental runes",
        kind: "orbit",
        description:
            "Runes circle you for a while, hitting whatever they pass and knocking it back.",
        iconObjId: RUNES[0],
        playerSeq: 711,
        sound: -1,
        hitSound: -1,
        rarity: "common",
        orbitObjIds: RUNES,
        levels: levels(
            {
                damage: 6,
                cooldown: 5,
                amount: 1,
                area: 2.2,
                pierce: 0,
                speed: 6,
                duration: 3,
                knockback: 0.6,
            },
            { amount: 2, description: "A water rune joins the air rune." },
            { damage: 7, description: "+1 damage." },
            { amount: 3, description: "An earth rune joins them." },
            { area: 2.6, duration: 3.5, description: "They circle wider, for longer." },
            { amount: 4, description: "A fire rune joins them." },
            { damage: 8, description: "+1 damage." },
            { speed: 7, duration: 4, description: "They go round faster, for longer." },
        ),
        evolution: { into: "astral_runes", requiresPassive: "vengeance" },
    },
    {
        id: "astral_runes",
        name: "Astral runes",
        kind: "orbit",
        description: "Evolved elemental runes. Vengeance's runes circle you and never stop.",
        iconObjId: 9075,
        playerSeq: 711,
        sound: -1,
        hitSound: -1,
        rarity: "veryRare",
        evolved: true,
        unlock: { kind: "killBoss", mapId: "fremennik", enemyId: "dagannoth_prime" },
        // Astral, death and earth: what Vengeance takes
        orbitObjIds: [9075, 560, 557],
        // They last as long as the wait to cast them again
        levels: evolvedLevels({
            damage: [9, 10, 11],
            cooldown: 4,
            amount: 6,
            area: 2.9,
            pierce: 0,
            speed: 7.5,
            duration: 4,
            knockback: 0.8,
            description: "Evolved from elemental runes with Vengeance.",
        }),
    },

    // Class weapons: only a character who fights that way finds them. Each
    // class has a Very rare one (the raids' best) and Rare ones, which start
    // stronger than the common weapons and have fewer levels. A new weapon is
    // never offered below its own rarity, so these come with bonus damage too.

    // Melee
    {
        id: "scythe_of_vitur",
        name: "Scythe of Vitur",
        kind: "sweep",
        description: "Sweeps a wide arc in front of you. Bosses and elites are hit three times.",
        iconObjId: 22325,
        playerSeq: 8056,
        // hacksword_slash stands in for the scythe's
        sound: 2500,
        hitSound: -1,
        rarity: "veryRare",
        style: "melee",
        evolution: { into: "sanguine_scythe", requiresPassive: "chivalry" },
        // Its second and third hits on big targets, like in OSRS
        bigHits: [0.5, 0.25],
        levels: levels(
            {
                damage: 38,
                cooldown: 1.25,
                amount: 1,
                area: 2.9,
                arc: 1.75,
                pierce: 0,
                speed: 0,
                duration: 0,
                knockback: 0.6,
                objId: 22325,
            },
            { damage: 45, description: "+7 damage." },
            { area: 3.3, description: "+14% reach." },
            { cooldown: 1.1, description: "Sweeps a little faster." },
            { damage: 54, description: "+9 damage." },
        ),
    },
    {
        // The Theatre's challenge mode reward: a scythe that sweeps all the way round
        id: "holy_scythe_of_vitur",
        name: "Holy Scythe of Vitur",
        kind: "sweep",
        description:
            "Sweeps all the way round you. Bosses and elites are hit three times, and every sixth sweep is Holy Light, which heals you.",
        iconObjId: 25736,
        playerSeq: 8056,
        // hacksword_slash stands in for the scythe's
        sound: 2500,
        hitSound: -1,
        rarity: "veryRare",
        style: "melee",
        bigHits: [0.5, 0.25],
        special: {
            name: "Holy Light",
            every: 6,
            damage: 1.25,
            playerSeq: 8056,
            // Blood Barrage's healing
            spotAnim: 1542,
            effect: { kind: "heal", fraction: 0.3, max: 2 },
        },
        unlock: { kind: "beatMap", mapId: "theatre_of_blood", hard: true },
        levels: levels(
            {
                damage: 30,
                cooldown: 1.45,
                amount: 1,
                area: 2.9,
                arc: Math.PI,
                pierce: 0,
                speed: 0,
                duration: 0,
                knockback: 0.6,
                objId: 25736,
            },
            { damage: 36, description: "+6 damage." },
            { area: 3.3, description: "+14% reach." },
            { cooldown: 1.3, description: "Sweeps a little faster." },
            { damage: 44, description: "+8 damage." },
        ),
    },
    {
        id: "sanguine_scythe",
        name: "Sanguine Scythe of Vitur",
        kind: "sweep",
        description:
            "Evolved Scythe of Vitur. A wider sweep. Bosses and elites are hit three times, and every fourth sweep is Blood Harvest, which heals you.",
        iconObjId: 25739,
        playerSeq: 8056,
        // hacksword_slash stands in for the scythe's
        sound: 2500,
        hitSound: -1,
        rarity: "veryRare",
        style: "melee",
        evolved: true,
        bigHits: [0.5, 0.25],
        special: {
            name: "Blood Harvest",
            every: 4,
            damage: 1.5,
            playerSeq: 8056,
            // Blood Barrage's healing
            spotAnim: 1542,
            effect: { kind: "heal", fraction: 0.5, max: 2 },
        },
        levels: evolvedLevels({
            damage: [55, 57, 61],
            cooldown: 1.1,
            amount: 1,
            area: 3.5,
            arc: 2.1,
            pierce: 0,
            speed: 0,
            duration: 0,
            knockback: 1,
            objId: 25739,
            description: "Evolved from the Scythe of Vitur with Chivalry.",
        }),
    },
    godsword(
        "armadyl_godsword",
        "Armadyl godsword",
        11802,
        "Every third swing is The Judgement, a huge hit.",
        {
            name: "The Judgement",
            every: 3,
            damage: 2.1,
            playerSeq: 7644,
            spotAnim: 1211,
        },
    ),
    godsword(
        "bandos_godsword",
        "Bandos godsword",
        11804,
        "Every third swing is Warstrike: what it hits takes 25% more damage for 10s.",
        {
            name: "Warstrike",
            every: 3,
            damage: 1.5,
            playerSeq: 7642,
            spotAnim: 1212,
            effect: { kind: "drain", amount: 0.25, seconds: 10 },
        },
    ),
    godsword(
        "saradomin_godsword",
        "Saradomin godsword",
        11806,
        "Every third swing is Healing Blade: heals you by half the damage it does, up to 3.",
        {
            name: "Healing Blade",
            every: 3,
            damage: 1.4,
            playerSeq: 7640,
            spotAnim: 1209,
            effect: { kind: "heal", fraction: 0.5, max: 3 },
        },
    ),
    godsword(
        "zamorak_godsword",
        "Zamorak godsword",
        11808,
        "Every third swing is Ice Cleave: what it hits is frozen in place.",
        {
            name: "Ice Cleave",
            every: 3,
            damage: 1.4,
            playerSeq: 7638,
            spotAnim: 1210,
            effect: { kind: "freeze", seconds: 2.5 },
        },
    ),

    // The godsword before its hilt: the player picks which one it becomes
    {
        id: "godsword",
        name: "Godsword",
        kind: "sweep",
        description: `A heavy two-handed sweep. At level ${HILT_LEVEL} you pick its hilt, and with it its special attack.`,
        iconObjId: 11798,
        playerSeq: GODSWORD_SEQ,
        sound: GODSWORD_SOUND,
        hitSound: -1,
        rarity: "rare",
        style: "melee",
        family: "godsword",
        evolution: { into: "ancient_godsword", requiresPassive: "piety" },
        hilts: {
            level: HILT_LEVEL,
            into: [
                { weaponId: "armadyl_godsword", hiltObjId: 11810 },
                { weaponId: "bandos_godsword", hiltObjId: 11812 },
                { weaponId: "saradomin_godsword", hiltObjId: 11814 },
                { weaponId: "zamorak_godsword", hiltObjId: 11816 },
            ],
        },
        // The blade alone can't be held, so it's only seen once it has a hilt
        levels: godswordLevels().map((level, i) =>
            i === HILT_LEVEL - 1 ? { ...level, description: "Pick a hilt." } : level,
        ),
        unlock: { kind: "beatMap", mapId: "god_wars" },
    },
    {
        id: "ancient_godsword",
        name: "Ancient godsword",
        kind: "sweep",
        description:
            "Evolved godsword. Every third swing is Blood Sacrifice: it hits everything around you and heals you.",
        iconObjId: 26233,
        playerSeq: GODSWORD_SEQ,
        sound: GODSWORD_SOUND,
        hitSound: -1,
        rarity: "veryRare",
        style: "melee",
        // Still the one godsword at a time
        family: "godsword",
        evolved: true,
        special: {
            name: "Blood Sacrifice",
            every: 3,
            damage: 1.6,
            playerSeq: 9171,
            // Blood Barrage's healing, on the player and what it hits
            spotAnim: 1542,
            aoe: { radius: 4.5, impactSpotAnim: 1542 },
            effect: { kind: "heal", fraction: 0.5, max: 3 },
        },
        levels: evolvedLevels({
            damage: 74,
            cooldown: 1.7,
            amount: 1,
            area: 3.4,
            arc: 0.95,
            pierce: 0,
            speed: 0,
            duration: 0,
            knockback: 1.4,
            objId: 26233,
            description: "Evolved from a godsword with Piety.",
        }),
    },
    {
        id: "dragon_claws",
        name: "Dragon claws",
        kind: "slash",
        description: "Four quick slashes at the closest enemy each time, the last the hardest.",
        iconObjId: 13652,
        // The claws' scratch; hacksword_slash stands in for its sound
        playerSeq: 393,
        sound: 2500,
        hitSound: -1,
        rarity: "rare",
        style: "melee",
        evolution: { into: "burning_claws", requiresPassive: "tome_of_fire" },
        hits: [0.4, 0.5, 0.6, 1],
        levels: levels(
            {
                damage: 11,
                cooldown: 1.2,
                amount: 1,
                area: 2.3,
                pierce: 0,
                speed: 0,
                duration: 0,
                knockback: 0.2,
                objId: 13652,
            },
            { damage: 14, description: "+3 damage a slash." },
            { area: 2.6, description: "+13% reach." },
            { cooldown: 1.05, description: "Slashes a little faster." },
            { damage: 16, amount: 2, description: "+2 damage a slash; also slashes behind you." },
        ),
        unlock: { kind: "killBoss", mapId: "morytania", enemyId: "pestilent_bloat" },
    },
    {
        id: "burning_claws",
        name: "Burning claws",
        kind: "slash",
        description:
            "Evolved Dragon claws. Four quick slashes each time, and every third set burns what it hits.",
        iconObjId: 29577,
        playerSeq: 393,
        sound: 2500,
        hitSound: -1,
        rarity: "veryRare",
        style: "melee",
        evolved: true,
        hits: [0.4, 0.5, 0.6, 1],
        special: {
            name: "Burning Slice",
            every: 3,
            damage: 1.25,
            // The claws' own special
            playerSeq: 7514,
            spotAnim: 1171,
            effect: { kind: "burn", damage: 0.4, seconds: 4 },
        },
        levels: evolvedLevels({
            damage: [17, 18, 19],
            cooldown: 1.05,
            amount: 2,
            area: 2.6,
            pierce: 0,
            speed: 0,
            duration: 0,
            knockback: 0.6,
            objId: 29577,
            description: "Evolved from the Dragon claws with the Tome of fire.",
        }),
    },
    {
        id: "ivandis_flail",
        name: "Ivandis flail",
        kind: "slash",
        description: "Flails at the closest enemy. Vampyres and the undead are hit 50% harder.",
        iconObjId: 22398,
        // Verac's flail; whip stands in for its sound
        playerSeq: 2062,
        sound: 2720,
        hitSound: -1,
        rarity: "rare",
        style: "melee",
        slayer: VAMPYRES_AND_UNDEAD,
        levels: levels(
            {
                damage: 20,
                cooldown: 1.1,
                amount: 1,
                area: 2.5,
                pierce: 0,
                speed: 0,
                duration: 0,
                knockback: 0.5,
                objId: 22398,
            },
            { damage: 24, description: "+4 damage." },
            { amount: 2, description: "Also flails behind you." },
            { cooldown: 1, area: 2.8, description: "Flails a little faster and further." },
            { damage: 29, description: "+5 damage." },
        ),
        evolution: { into: "blisterwood_flail", requiresPassive: "book_of_the_dead" },
        unlock: { kind: "beatMap", mapId: "morytania" },
    },
    {
        id: "blisterwood_flail",
        name: "Blisterwood flail",
        kind: "slash",
        description:
            "Evolved Ivandis flail. Vampyres and the undead are hit 50% harder, and pass the hit on to three more of their kind nearby.",
        iconObjId: 24699,
        playerSeq: 2062,
        sound: 2720,
        hitSound: -1,
        rarity: "veryRare",
        evolved: true,
        slayer: VAMPYRES_AND_UNDEAD,
        // Crumble Undead's impact on each one it passes to
        chain: { tags: ["vampyre", "undead"], count: 3, range: 4, damage: 0.6, spotAnim: 147 },
        levels: evolvedLevels({
            damage: [30, 32, 34],
            cooldown: 0.95,
            amount: 2,
            area: 3,
            pierce: 0,
            speed: 0,
            duration: 0,
            knockback: 0.9,
            objId: 24699,
            description: "Evolved from the Ivandis flail with the Book of the dead.",
        }),
    },
    {
        id: "osmumtens_fang",
        name: "Osmumten's fang",
        kind: "slash",
        description: "Fast, precise stabs at the closest enemy. Bosses and elites are hit twice.",
        iconObjId: 26219,
        // A stab; hacksword_slash stands in for its sound
        playerSeq: 386,
        sound: 2500,
        hitSound: -1,
        rarity: "rare",
        style: "melee",
        // It rolls twice to hit in OSRS
        bigHits: [0.6],
        unlock: { kind: "beatMap", mapId: "desert" },
        levels: levels(
            {
                damage: 24,
                cooldown: 0.85,
                amount: 1,
                area: 2.3,
                pierce: 0,
                speed: 0,
                duration: 0,
                knockback: 0.3,
                objId: 26219,
            },
            { damage: 29, description: "+5 damage." },
            { area: 2.6, description: "+13% reach." },
            { cooldown: 0.75, description: "Stabs a little faster." },
            { damage: 35, description: "+6 damage." },
        ),
    },
    {
        id: "ghrazi_rapier",
        name: "Ghrazi rapier",
        kind: "slash",
        description:
            "The fastest stabs there are, at the closest enemy. Bosses and elites are hit twice.",
        iconObjId: 22324,
        // A stab; hacksword_slash stands in for its sound
        playerSeq: 386,
        sound: 2500,
        hitSound: -1,
        rarity: "veryRare",
        style: "melee",
        bigHits: [0.6],
        unlock: { kind: "beatMap", mapId: "theatre_of_blood" },
        levels: levels(
            {
                damage: 24,
                cooldown: 0.66,
                amount: 1,
                area: 2.3,
                pierce: 0,
                speed: 0,
                duration: 0,
                knockback: 0.2,
                objId: 22324,
            },
            { damage: 29, description: "+5 damage." },
            { area: 2.6, description: "+13% reach." },
            { cooldown: 0.58, description: "Stabs faster." },
            { damage: 36, description: "+7 damage." },
        ),
    },
    {
        id: "tzhaar_ket_om",
        name: "Tzhaar-ket-om",
        kind: "sweep",
        description:
            "A slow, crushing sweep that knocks enemies far back. Every fourth swing is Quake: what it hits is stunned.",
        iconObjId: 6528,
        // A maul; hacksword_slash stands in for its sound
        playerSeq: 2661,
        sound: 2500,
        hitSound: -1,
        rarity: "rare",
        style: "melee",
        unlock: { kind: "beatMap", mapId: "karamja" },
        special: {
            name: "Quake",
            every: 4,
            damage: 1.3,
            playerSeq: 2661,
            // Fire Wave's impact, a burst of magma
            spotAnim: 157,
            effect: { kind: "freeze", seconds: 2 },
        },
        levels: levels(
            {
                damage: 53,
                cooldown: 2.1,
                amount: 1,
                area: 2.7,
                arc: 0.85,
                pierce: 0,
                speed: 0,
                duration: 0,
                knockback: 2,
                objId: 6528,
            },
            { damage: 62, description: "+9 damage." },
            { area: 3, description: "+11% reach." },
            { cooldown: 1.9, description: "Swings a little faster." },
            { damage: 75, description: "+13 damage." },
        ),
    },
    {
        id: "dragon_warhammer",
        name: "Dragon warhammer",
        kind: "slash",
        description:
            "Heavy blows at the closest enemy. Every third is Smash: what it hits takes 30% more damage for 8s.",
        iconObjId: 13576,
        // hacksword_slash stands in for its sound
        playerSeq: 401,
        sound: 2500,
        hitSound: -1,
        rarity: "rare",
        style: "melee",
        evolution: { into: "elder_maul", requiresPassive: "protect_melee" },
        unlock: { kind: "killBoss", mapId: "kourend", enemyId: "lizardman_shaman" },
        special: {
            name: "Smash",
            every: 3,
            damage: 1.5,
            playerSeq: 1378,
            spotAnim: 1292,
            effect: { kind: "drain", amount: 0.3, seconds: 8 },
        },
        levels: levels(
            {
                damage: 36,
                cooldown: 1.4,
                amount: 1,
                area: 2.3,
                pierce: 0,
                speed: 0,
                duration: 0,
                knockback: 0.8,
                objId: 13576,
            },
            { damage: 42, description: "+6 damage." },
            { area: 2.6, description: "+13% reach." },
            { cooldown: 1.25, description: "Swings a little faster." },
            { damage: 52, description: "+10 damage." },
        ),
    },
    {
        id: "elder_maul",
        name: "Elder maul",
        kind: "sweep",
        description:
            "Evolved Dragon warhammer. A heavy sweep. Every third is Shockwave: it hits everything around you, and they take 30% more damage for 8s.",
        iconObjId: 21003,
        playerSeq: 7516,
        // hacksword_slash stands in for its sound
        sound: 2500,
        hitSound: -1,
        rarity: "veryRare",
        style: "melee",
        evolved: true,
        special: {
            name: "Shockwave",
            every: 3,
            damage: 1.5,
            playerSeq: 7516,
            spotAnim: 1292,
            aoe: { radius: 4 },
            effect: { kind: "drain", amount: 0.3, seconds: 8 },
        },
        levels: evolvedLevels({
            damage: [66, 70, 75],
            cooldown: 1.5,
            amount: 1,
            area: 2.9,
            arc: 1.2,
            pierce: 0,
            speed: 0,
            duration: 0,
            knockback: 1.2,
            objId: 21003,
            description: "Evolved from the Dragon warhammer with Protect from Melee.",
        }),
    },
    {
        id: "dinhs_bulwark",
        name: "Dinh's bulwark",
        kind: "sweep",
        description: "A slow, heavy bash that hits everything around you, far out.",
        iconObjId: 21015,
        playerSeq: 7511,
        // hacksword_slash stands in for its sound
        sound: 2500,
        hitSound: -1,
        rarity: "rare",
        style: "melee",
        evolution: { into: "dinhs_blazing_bulwark", requiresPassive: "vengeance" },
        unlock: { kind: "beatMap", mapId: "kourend" },
        levels: levels(
            {
                damage: 60,
                cooldown: 3.4,
                amount: 1,
                area: 4,
                // All the way round
                arc: Math.PI,
                pierce: 0,
                speed: 0,
                duration: 0,
                knockback: 1.2,
                objId: 21015,
            },
            { damage: 78, description: "+18 damage." },
            { area: 4.6, description: "+15% reach." },
            { cooldown: 3, description: "Bashes a little faster." },
            { damage: 96, description: "+18 damage." },
        ),
    },
    {
        id: "dinhs_blazing_bulwark",
        name: "Dinh's blazing bulwark",
        kind: "sweep",
        description:
            "Evolved Dinh's bulwark. A slow bash all around you. Every third is Shield Bash: it reaches further and burns what it hits.",
        iconObjId: 28682,
        playerSeq: 7511,
        // hacksword_slash stands in for its sound
        sound: 2500,
        hitSound: -1,
        rarity: "veryRare",
        style: "melee",
        evolved: true,
        special: {
            name: "Shield Bash",
            every: 3,
            damage: 1.4,
            playerSeq: 7511,
            // Fire Wave's cast and impact
            spotAnim: 155,
            aoe: { radius: 6.5, impactSpotAnim: 157 },
            effect: { kind: "burn", damage: 0.3, seconds: 4 },
        },
        levels: evolvedLevels({
            damage: [97, 98, 99],
            cooldown: 2.8,
            amount: 1,
            area: 5,
            // All the way round
            arc: Math.PI,
            pierce: 0,
            speed: 0,
            duration: 0,
            knockback: 1.6,
            objId: 28682,
            description: "Evolved from Dinh's bulwark with Vengeance.",
        }),
    },
    {
        id: "dual_macuahuitl",
        name: "Dual macuahuitl",
        kind: "slash",
        description: "Very fast slashes on both sides of you.",
        iconObjId: 28997,
        playerSeq: 390,
        // hacksword_slash
        sound: 2500,
        hitSound: -1,
        rarity: "rare",
        style: "melee",
        unlock: { kind: "killBoss", mapId: "varlamore", enemyId: "manticore" },
        levels: levels(
            {
                damage: 14,
                cooldown: 0.75,
                amount: 2,
                area: 2.1,
                pierce: 0,
                speed: 0,
                duration: 0,
                knockback: 0.4,
                objId: 28997,
            },
            { damage: 16, description: "+2 damage." },
            { cooldown: 0.65, description: "Slashes a little faster." },
            { area: 2.4, description: "+14% reach." },
            { damage: 20, description: "+4 damage." },
        ),
    },

    {
        id: "thunder_khopesh",
        name: "Thunder khopesh",
        kind: "slash",
        description:
            "Quick slashes at the closest enemy. Every third is Thunderclap: it hits everything around you.",
        iconObjId: 30388,
        playerSeq: 390,
        // hacksword_slash
        sound: 2500,
        hitSound: -1,
        rarity: "rare",
        style: "melee",
        unlock: { kind: "beatMap", mapId: "varlamore" },
        special: {
            name: "Thunderclap",
            every: 3,
            damage: 1.6,
            // The Dragon 2h sword's Powerstab, which hits all round too
            playerSeq: 3157,
            spotAnim: 559,
            // Saradomin Strike's lightning on what it hits
            aoe: { radius: 3.5, impactSpotAnim: 76 },
        },
        levels: levels(
            {
                damage: 26,
                cooldown: 1.05,
                amount: 1,
                area: 2.4,
                pierce: 0,
                speed: 0,
                duration: 0,
                knockback: 0.6,
                objId: 30388,
            },
            { damage: 30, description: "+4 damage." },
            { area: 2.7, description: "+13% reach." },
            { cooldown: 0.9, description: "Slashes a little faster." },
            { damage: 38, description: "+8 damage." },
        ),
    },

    // Ranged. Arrows fly like spells do: at their target, and on through it
    // if they pierce. throwingknife stands in for a bow's sound.
    {
        id: "twisted_bow",
        name: "Twisted bow",
        kind: "missile",
        description:
            "Shoots the toughest enemy in range. The more hitpoints it has, the harder it hits: up to +150% on bosses.",
        iconObjId: 20997,
        playerSeq: 426,
        sound: 2707,
        hitSound: -1,
        rarity: "veryRare",
        style: "ranged",
        evolution: { into: "corrupted_twisted_bow", requiresPassive: "rigour" },
        toughTargets: 1.5,
        levels: levels(
            {
                damage: 18,
                cooldown: 1.1,
                amount: 1,
                area: 0,
                pierce: 0,
                speed: 16,
                duration: 0,
                knockback: 0.3,
                objId: 20997,
                // Amethyst arrows
                projectileSpotAnim: 1384,
            },
            { amount: 2, description: "Fires one more arrow." },
            { damage: 21, description: "+3 damage." },
            { cooldown: 1, description: "Fires a little faster." },
            { amount: 3, description: "Fires one more arrow." },
        ),
    },
    {
        id: "corrupted_twisted_bow",
        name: "Corrupted twisted bow",
        kind: "missile",
        description:
            "Evolved Twisted bow. Shoots the toughest enemy in range, up to +180% on bosses, and bosses and elites are hit again.",
        iconObjId: 28540,
        playerSeq: 426,
        sound: 2707,
        hitSound: -1,
        rarity: "veryRare",
        style: "ranged",
        evolved: true,
        toughTargets: 1.8,
        bigHits: [0.35],
        levels: evolvedLevels({
            damage: [22, 24, 25],
            cooldown: 1,
            amount: 3,
            area: 0,
            pierce: 0,
            speed: 16,
            duration: 0,
            knockback: 0.3,
            objId: 28540,
            // Amethyst arrows
            projectileSpotAnim: 1384,
            description: "Evolved from the Twisted bow with Rigour.",
        }),
    },
    {
        id: "bow_of_faerdhinen",
        name: "Bow of Faerdhinen",
        kind: "missile",
        description: "Fires crystal arrows at the nearest enemies that fly on through them.",
        iconObjId: 25865,
        playerSeq: 426,
        sound: 2707,
        hitSound: -1,
        rarity: "rare",
        style: "ranged",
        levels: levels(
            {
                damage: 10,
                cooldown: 0.75,
                amount: 1,
                area: 0,
                pierce: 1,
                speed: 18,
                duration: 0,
                knockback: 0.2,
                objId: 25865,
                projectileSpotAnim: 1887,
            },
            { amount: 2, description: "Fires one more arrow." },
            { damage: 13, description: "+3 damage." },
            { pierce: 3, description: "Arrows pass through two more enemies." },
            { amount: 3, cooldown: 0.65, description: "Fires one more arrow, a little faster." },
        ),
    },
    {
        id: "toxic_blowpipe",
        name: "Toxic blowpipe",
        kind: "missile",
        description:
            "Fires darts that leave venom in what they hit, hurting it every second. Goes for enemies without venom first, so it spreads.",
        iconObjId: 12926,
        playerSeq: 5061,
        sound: 2707,
        hitSound: -1,
        rarity: "rare",
        style: "ranged",
        levels: levels(
            {
                damage: 5,
                cooldown: 0.45,
                amount: 1,
                area: 0,
                pierce: 0,
                speed: 18,
                duration: 3,
                venom: 3,
                knockback: 0.1,
                objId: 12926,
                // Dragon darts
                projectileSpotAnim: 1122,
            },
            { venom: 4, description: "Stronger venom: 4 damage a second." },
            { amount: 2, description: "Fires one more dart." },
            { damage: 7, cooldown: 0.35, description: "+2 damage, fires faster." },
            { venom: 6, duration: 4, description: "Stronger venom that lasts longer." },
        ),
    },
    {
        id: "dragon_hunter_crossbow",
        name: "Dragon hunter crossbow",
        kind: "missile",
        description:
            "Fires heavy bolts at the nearest enemy that pass through it. Bosses and elites are hit again.",
        iconObjId: 21012,
        playerSeq: 4230,
        sound: 2707,
        hitSound: -1,
        rarity: "rare",
        style: "ranged",
        bigHits: [0.5],
        unlock: { kind: "beatMap", mapId: "fremennik" },
        levels: levels(
            {
                damage: 15,
                cooldown: 1.15,
                amount: 1,
                area: 0,
                pierce: 1,
                speed: 16,
                duration: 0,
                knockback: 0.5,
                objId: 21012,
                // A crossbow bolt
                projectileSpotAnim: 27,
            },
            { damage: 18, description: "+3 damage." },
            { amount: 2, description: "Fires one more bolt." },
            { cooldown: 1, description: "Fires a little faster." },
            { damage: 23, description: "+5 damage." },
        ),
    },
    {
        id: "armadyl_crossbow",
        name: "Armadyl crossbow",
        kind: "missile",
        description:
            "Fires bolts at the nearest enemy that pass through it. One in five is enchanted: a ruby's takes a fifth of what's left of its hitpoints, a diamond's goes on through more, a dragonstone's burns.",
        iconObjId: 11785,
        playerSeq: 4230,
        sound: 2707,
        hitSound: -1,
        rarity: "rare",
        style: "ranged",
        boltProcs: {
            chance: 1 / 5,
            procs: [
                {
                    name: "Blood Forfeit",
                    weight: 1,
                    spotAnim: 754,
                    effect: { kind: "hpFraction", fraction: 0.2, max: 4 },
                },
                {
                    name: "Armour Piercing",
                    weight: 1,
                    spotAnim: 758,
                    effect: { kind: "pierce", damage: 0.5, pierce: 3 },
                },
                {
                    name: "Dragon's Breath",
                    weight: 1,
                    spotAnim: 756,
                    effect: { kind: "burn", damage: 0.4, seconds: 4 },
                },
            ],
        },
        levels: levels(
            {
                damage: 15,
                cooldown: 1,
                amount: 1,
                area: 0,
                pierce: 1,
                speed: 17,
                duration: 0,
                knockback: 0.4,
                objId: 11785,
                // A crossbow bolt
                projectileSpotAnim: 27,
            },
            { damage: 18, description: "+3 damage." },
            { amount: 2, description: "Fires one more bolt." },
            { cooldown: 0.85, description: "Fires a little faster." },
            { damage: 23, description: "+5 damage." },
        ),
        unlock: { kind: "killBoss", mapId: "god_wars", enemyId: "kreearra" },
    },
    {
        id: "crystal_bow",
        name: "Crystal bow",
        kind: "missile",
        description:
            "Fires crystal arrows at the nearest enemy. Each splits into three where it first hits, flying on through the enemies around it.",
        iconObjId: 23983,
        playerSeq: 426,
        sound: 2707,
        hitSound: -1,
        rarity: "rare",
        style: "ranged",
        split: { count: 3, damage: 0.35, pierce: 1 },
        levels: levels(
            {
                damage: 10,
                cooldown: 0.9,
                amount: 1,
                area: 0,
                pierce: 0,
                speed: 17,
                duration: 0,
                knockback: 0.2,
                objId: 23983,
                // Crystal arrows
                projectileSpotAnim: 249,
            },
            { damage: 12, description: "+2 damage." },
            { amount: 2, description: "Fires one more arrow." },
            { cooldown: 0.8, description: "Fires a little faster." },
            { damage: 14, description: "+2 damage." },
        ),
        evolution: { into: "corrupted_bow", requiresPassive: "graceful" },
        unlock: { kind: "beatMap", mapId: "prifddinas" },
    },
    {
        id: "corrupted_bow",
        name: "Corrupted bow (perfected)",
        kind: "missile",
        description:
            "Evolved crystal bow. Corrupted arrows split into five where they first hit, and each goes on through one more.",
        iconObjId: 23857,
        playerSeq: 426,
        sound: 2707,
        hitSound: -1,
        rarity: "veryRare",
        evolved: true,
        split: { count: 5, damage: 0.35, pierce: 1 },
        levels: evolvedLevels({
            damage: [17, 18, 19],
            cooldown: 0.8,
            amount: 2,
            area: 0,
            pierce: 0,
            speed: 18,
            duration: 0,
            knockback: 0.3,
            objId: 23857,
            // The Corrupted Hunllef's crystal, in red
            projectileSpotAnim: 1702,
            impactSpotAnim: 1704,
            description: "Evolved from the crystal bow with the Graceful outfit.",
        }),
    },
    {
        id: "eclipse_atlatl",
        name: "Eclipse atlatl",
        kind: "knives",
        description: "Throws darts that pass through enemies and set what they hit burning.",
        iconObjId: 29000,
        // throwingknife stands in for its sound
        playerSeq: 806,
        sound: 2707,
        hitSound: -1,
        rarity: "rare",
        style: "ranged",
        unlock: { kind: "killBoss", mapId: "varlamore", enemyId: "javelin_colossus" },
        levels: levels(
            {
                damage: 8,
                cooldown: 0.7,
                amount: 2,
                area: 0,
                pierce: 1,
                speed: 15,
                duration: 3,
                venom: 2,
                knockback: 0.2,
                objId: 29000,
                // Atlatl darts
                projectileObjId: 28991,
            },
            { venom: 3, description: "Burns hotter: 3 damage a second." },
            { amount: 3, description: "Throws one more dart." },
            { damage: 10, description: "+2 damage." },
            { cooldown: 0.6, venom: 4, description: "Throws faster; burns hotter." },
        ),
    },

    // Magic
    {
        id: "tumekens_shadow",
        name: "Tumeken's shadow",
        kind: "missile",
        description: "Fires shadow blasts that splash. Triples your damage bonus for its hits.",
        iconObjId: 27275,
        playerSeq: 9493,
        // Fire Wave's sounds stand in for the shadow's
        sound: 162,
        hitSound: 163,
        rarity: "veryRare",
        style: "magic",
        evolution: { into: "corrupted_tumekens_shadow", requiresPassive: "augury" },
        mightScale: 3,
        levels: levels(
            {
                damage: 24,
                cooldown: 1.4,
                amount: 1,
                area: 1,
                pierce: 0,
                speed: 10,
                duration: 0,
                knockback: 0.4,
                objId: 27275,
                castSpotAnim: 2125,
                projectileSpotAnim: 2126,
                impactSpotAnim: 2127,
            },
            { amount: 2, description: "Casts one more blast." },
            { damage: 30, description: "+6 damage." },
            { area: 1.3, pierce: 1, description: "Bigger splash; blasts pass through an enemy." },
            { amount: 3, cooldown: 1.25, description: "Casts one more blast, a little faster." },
        ),
    },
    {
        id: "corrupted_tumekens_shadow",
        name: "Corrupted tumeken's shadow",
        kind: "missile",
        description:
            "Evolved Tumeken's shadow. Bigger blasts that pass through two enemies. 3.5x your damage bonus for its hits.",
        iconObjId: 28547,
        playerSeq: 9493,
        // Fire Wave's sounds stand in for the shadow's
        sound: 162,
        hitSound: 163,
        rarity: "veryRare",
        style: "magic",
        evolved: true,
        mightScale: 3.5,
        levels: evolvedLevels({
            damage: [31, 32, 34],
            cooldown: 1.2,
            amount: 3,
            area: 1.6,
            pierce: 2,
            speed: 10,
            duration: 0,
            knockback: 0.4,
            objId: 28547,
            castSpotAnim: 2125,
            projectileSpotAnim: 2126,
            impactSpotAnim: 2127,
            description: "Evolved from Tumeken's shadow with Augury.",
        }),
    },
    {
        id: "harmonised_nightmare_staff",
        name: "Harmonised nightmare staff",
        kind: "missile",
        description: "Casts Fire Surge at the nearest enemy, quicker than any other staff.",
        iconObjId: 24423,
        playerSeq: 7855,
        sound: FIRE_SURGE.sound,
        hitSound: FIRE_SURGE.hitSound,
        rarity: "rare",
        style: "magic",
        evolution: { into: "eldritch_nightmare_staff", requiresPassive: "amulet_of_glory" },
        levels: levels(
            {
                ...FIRE_SURGE,
                damage: 10,
                cooldown: 0.8,
                amount: 1,
                area: 0.8,
                pierce: 0,
                speed: 11,
                duration: 0,
                knockback: 0.25,
                objId: 24423,
            },
            { damage: 12, description: "+2 damage." },
            { amount: 2, description: "Casts one more Fire Surge." },
            { cooldown: 0.7, description: "Casts a little faster." },
            { area: 1, pierce: 1, description: "Bigger splash; surges pass through an enemy." },
        ),
    },
    {
        id: "eldritch_nightmare_staff",
        name: "Eldritch nightmare staff",
        kind: "missile",
        description:
            "Evolved Harmonised nightmare staff. Each Fire Surge splits into three more where it first hits.",
        iconObjId: 24425,
        playerSeq: 7855,
        sound: FIRE_SURGE.sound,
        hitSound: FIRE_SURGE.hitSound,
        rarity: "veryRare",
        style: "magic",
        evolved: true,
        split: { count: 3, damage: 0.4, pierce: 0 },
        levels: evolvedLevels({
            ...FIRE_SURGE,
            damage: [13, 14, 15],
            cooldown: 0.7,
            amount: 2,
            area: 1,
            pierce: 1,
            speed: 11,
            duration: 0,
            knockback: 0.25,
            objId: 24425,
            description: "Evolved from the Harmonised nightmare staff with the Amulet of glory.",
        }),
    },
    {
        // Phosani's own: her grasping claws, turned on what's around the player
        id: "nightmare_staff",
        name: "Nightmare staff",
        kind: "strike",
        description:
            "The Nightmare's grasping claws come up out of the ground under enemies around you, every so often. Bosses and elites are grabbed twice more.",
        iconObjId: 24422,
        // The staff's cast; her claws' sound, and her hands (spot 1761) coming
        // up in full where each lands, as they do in her fight
        playerSeq: 7855,
        sound: 4274,
        hitSound: -1,
        rarity: "rare",
        style: "magic",
        bigHits: [0.75, 0.75],
        impactFit: 1,
        unlock: { kind: "killBoss", mapId: "phosani", enemyId: "phosanis_nightmare" },
        levels: levels(
            {
                damage: 20,
                cooldown: 2.8,
                amount: 3,
                area: 0.9,
                pierce: 0,
                speed: 0,
                duration: 0,
                knockback: 0.5,
                objId: 24422,
                impactSpotAnim: 1761,
            },
            { amount: 4, description: "One more hand." },
            { damage: 24, description: "+4 damage." },
            { area: 1.2, description: "Wider hands." },
            { cooldown: 2.4, description: "The hands come a little more often." },
            { amount: 5, description: "One more hand." },
            { damage: 28, description: "+4 damage." },
            { amount: 6, description: "One more hand." },
        ),
    },
    {
        id: "volatile_nightmare_staff",
        name: "Volatile Nightmare staff",
        kind: "missile",
        description:
            "Casts Immolate at the toughest enemy in range: slow, and the bigger the target, the harder it burns.",
        iconObjId: 24424,
        playerSeq: 8532,
        sound: FIRE_SURGE.sound,
        hitSound: FIRE_SURGE.hitSound,
        rarity: "rare",
        style: "magic",
        toughTargets: 1.2,
        unlock: { kind: "killBoss", mapId: "halloween", enemyId: "the_nightmare" },
        levels: levels(
            {
                ...FIRE_SURGE,
                castSpotAnim: 1760,
                impactSpotAnim: 1759,
                damage: 30,
                cooldown: 1.7,
                amount: 1,
                area: 1,
                pierce: 0,
                speed: 16,
                duration: 0,
                knockback: 0.3,
                objId: 24424,
            },
            { damage: 36, description: "+6 damage." },
            { cooldown: 1.5, description: "Casts a little faster." },
            { area: 1.3, description: "Bigger splash." },
            { damage: 44, amount: 2, description: "+8 damage; casts one more Immolate." },
        ),
    },
    {
        id: "inquisitors_mace",
        name: "Inquisitor's mace",
        kind: "slash",
        description:
            "Crushing blows at the closest enemy, a quarter harder on the undead. Bosses and elites are hit again.",
        iconObjId: 24417,
        playerSeq: 4503,
        // hacksword_slash stands in for the mace's
        sound: 2500,
        hitSound: -1,
        rarity: "rare",
        style: "melee",
        slayer: { tags: ["undead"], damage: 0.25 },
        bigHits: [0.4],
        unlock: { kind: "killBoss", mapId: "halloween", enemyId: "the_nightmare" },
        levels: levels(
            {
                damage: 26,
                cooldown: 1,
                amount: 1,
                area: 2.4,
                pierce: 0,
                speed: 0,
                duration: 0,
                knockback: 0.4,
                objId: 24417,
            },
            { damage: 32, description: "+6 damage." },
            { area: 2.7, description: "+13% reach." },
            { cooldown: 0.9, description: "Swings a little faster." },
            { damage: 38, amount: 2, description: "+6 damage; also swings behind you." },
        ),
    },
    {
        id: "sanguinesti_staff",
        name: "Sanguinesti staff",
        kind: "missile",
        description:
            "Casts blood blasts at the nearest enemy. One hit in six heals you for half its damage, up to 2 hitpoints.",
        iconObjId: 22323,
        // The trident's cast; Fire Wave's sounds stand in for the staff's
        playerSeq: 1167,
        sound: 162,
        hitSound: 163,
        rarity: "rare",
        style: "magic",
        evolution: { into: "holy_sanguinesti_staff", requiresPassive: "rapid_heal" },
        lifesteal: { chance: 1 / 6, fraction: 0.5, max: 2, spotAnim: 1542 },
        levels: levels(
            {
                damage: 16,
                cooldown: 1.2,
                amount: 1,
                area: 0,
                pierce: 0,
                speed: 10,
                duration: 0,
                knockback: 0.4,
                objId: 22323,
                castSpotAnim: 1540,
                projectileSpotAnim: 1539,
                impactSpotAnim: 1541,
            },
            { damage: 19, description: "+3 damage." },
            { amount: 2, description: "Casts one more blast." },
            { cooldown: 1.05, pierce: 1, description: "Faster; blasts pass through an enemy." },
            { damage: 24, description: "+5 damage." },
        ),
    },
    {
        id: "holy_sanguinesti_staff",
        name: "Holy sanguinesti staff",
        kind: "missile",
        description:
            "Evolved Sanguinesti staff. Blood blasts that splash. One hit in four heals you for half its damage, up to 3 hitpoints.",
        iconObjId: 25731,
        playerSeq: 1167,
        // Fire Wave's sounds stand in for the staff's
        sound: 162,
        hitSound: 163,
        rarity: "veryRare",
        style: "magic",
        evolved: true,
        lifesteal: { chance: 1 / 4, fraction: 0.5, max: 3, spotAnim: 1542 },
        levels: evolvedLevels({
            damage: [26, 28, 29],
            cooldown: 1.05,
            amount: 2,
            area: 0.9,
            pierce: 1,
            speed: 10,
            duration: 0,
            knockback: 0.4,
            objId: 25731,
            castSpotAnim: 1540,
            projectileSpotAnim: 1539,
            impactSpotAnim: 1541,
            description: "Evolved from the Sanguinesti staff with Rapid Heal.",
        }),
    },
    {
        id: "kodai_wand",
        name: "Kodai wand",
        kind: "missile",
        description: "Casts Water Surge at the nearest enemies, two at a time, with a splash.",
        iconObjId: 21006,
        // The staff cast; Fire Wave's sounds stand in for Water Surge's
        playerSeq: 7855,
        sound: 162,
        hitSound: 163,
        rarity: "rare",
        style: "magic",
        unlock: { kind: "beatMap", mapId: "kourend" },
        levels: levels(
            {
                damage: 13,
                cooldown: 1.1,
                amount: 2,
                area: 1,
                pierce: 0,
                speed: 11,
                duration: 0,
                knockback: 0.3,
                objId: 21006,
                castSpotAnim: 1458,
                projectileSpotAnim: 1459,
                impactSpotAnim: 1460,
            },
            { damage: 15, description: "+2 damage." },
            { amount: 3, description: "Casts one more Water Surge." },
            { cooldown: 0.95, description: "Casts a little faster." },
            { damage: 18, area: 1.3, description: "+3 damage, bigger splash." },
        ),
    },
    {
        id: "crystal_staff",
        name: "Crystal staff",
        kind: "missile",
        description:
            "Casts crystal spells at the nearest enemy. Starts basic and grows: attuned at level 4, perfected at 7.",
        iconObjId: 23898,
        // The trident's cast; Fire Wave's sounds stand in for the staff's
        playerSeq: 1167,
        sound: 162,
        hitSound: 163,
        rarity: "rare",
        style: "magic",
        levels: levels(
            {
                name: "Crystal staff (basic)",
                ...CRYSTAL_STAFF.basic,
                damage: 10,
                cooldown: 0.95,
                amount: 1,
                area: 0.7,
                pierce: 0,
                speed: 11,
                duration: 0,
                knockback: 0.3,
                objId: 23898,
            },
            { damage: 11, description: "+1 damage." },
            { amount: 2, description: "Casts one more spell." },
            {
                name: "Crystal staff (attuned)",
                ...CRYSTAL_STAFF.attuned,
                objId: 23899,
                damage: 14,
                area: 0.9,
                description: "Attuned: +3 damage, bigger splash.",
            },
            { cooldown: 0.9, description: "Casts a little faster." },
            { amount: 3, description: "Casts one more spell." },
            {
                name: "Crystal staff (perfected)",
                ...CRYSTAL_STAFF.perfected,
                objId: 23900,
                damage: 16,
                area: 1.1,
                description: "Perfected: +2 damage, bigger splash.",
            },
            { damage: 18, cooldown: 0.85, description: "+2 damage, casts a little faster." },
        ),
        unlock: { kind: "survive", mapId: "prifddinas", seconds: 10 * 60 },
    },
    {
        id: "toktz_mej_tal",
        name: "Toktz-mej-tal",
        kind: "aura",
        description:
            "Pulses of obsidian heat around you that leave the ground burning for a while, hurting whatever walks through.",
        iconObjId: 6526,
        // The Ancient cast, and an Ancient burst's sound
        playerSeq: 1979,
        sound: 170,
        hitSound: -1,
        rarity: "rare",
        style: "magic",
        // Fire, the plain flames with no logs or stones under them
        burns: { damage: 0.3, radius: 0.8, locId: 5499 },
        levels: levels(
            {
                damage: 7,
                cooldown: 2,
                amount: 1,
                area: 2.4,
                pierce: 0,
                speed: 0,
                duration: 2.5,
                knockback: 0.2,
                objId: 6526,
                impactSpotAnim: 1482,
            },
            { area: 2.7, description: "+13% radius." },
            { damage: 10, description: "+3 damage." },
            { cooldown: 1.8, description: "Pulses a little more often." },
            { damage: 13, duration: 3.5, description: "+3 damage; the ground burns for longer." },
        ),
        unlock: { kind: "killBoss", mapId: "inferno", enemyId: "jaltok_jad" },
    },
    {
        id: "blue_moon_spear",
        name: "Blue moon spear",
        kind: "strike",
        description: "Ice crashes down on enemies around you, freezing them.",
        iconObjId: 28988,
        playerSeq: 1979,
        // ice_burst_impact
        sound: 170,
        hitSound: -1,
        rarity: "rare",
        style: "magic",
        freezes: true,
        unlock: { kind: "killBoss", mapId: "varlamore", enemyId: "shockwave_colossus" },
        levels: levels(
            {
                damage: 16,
                cooldown: 2.6,
                amount: 3,
                area: 0.8,
                pierce: 0,
                speed: 0,
                duration: 1,
                knockback: 0,
                objId: 28988,
                impactSpotAnim: 363,
            },
            { amount: 4, description: "Strikes one more enemy." },
            { damage: 19, description: "+3 damage." },
            { duration: 1.5, description: "Freezes for longer." },
            {
                amount: 5,
                cooldown: 2.3,
                description: "Strikes one more enemy, a little more often.",
            },
        ),
    },
];

// Every other level of a melee weapon pushes harder
const KNOCKBACK_STEP = 0.2;
for (const weapon of WEAPONS) {
    if (weapon.kind === "slash" || weapon.kind === "sweep") {
        weapon.levels = weapon.levels.map((level, i) => ({
            ...level,
            knockback: level.knockback + KNOCKBACK_STEP * Math.floor(i / 2),
            description:
                i > 0 && i % 2 === 0
                    ? `${level.description} +${KNOCKBACK_STEP} knockback.`
                    : level.description,
        }));
    }
}

export const WEAPON_BY_ID: Map<string, WeaponDef> = new Map(WEAPONS.map((w) => [w.id, w]));

export function getWeapon(id: string): WeaponDef {
    const weapon = WEAPON_BY_ID.get(id);
    if (!weapon) {
        throw new Error("Unknown weapon " + id);
    }
    return weapon;
}

// The style a weapon's hits count as (see WeaponDef.damageStyle)
export function getWeaponStyle(weapon: WeaponDef): CombatStyle {
    const style = weapon.damageStyle ?? weapon.style;
    if (style) {
        return style;
    }
    switch (weapon.kind) {
        case "slash":
        case "sweep":
            return "melee";
        case "missile":
        case "knives":
        case "axes":
            return "ranged";
        case "aura":
        case "strike":
        case "orbit":
            return "magic";
    }
}
